/**
 * Naehrwerte rechnen (F-35) - rein und getestet: Lebensmittel nach Menge,
 * Rezept pro Portion, Mahlzeit und Tag. Unbekannte Werte bleiben unbekannt
 * und werden als „unvollstaendig“ gemeldet, statt als 0 zu zaehlen.
 */
import { MAKROS, MIKROS, type Getraenk, type Lebensmittel, type MakroKey, type Mahlzeit, type Mikro, type Naehrwerte, type Posten, type Rezept } from '@/domain/ernaehrung/typen';
import type { ID } from '@/domain/types';

export const MAKRO_KEYS = Object.keys(MAKROS) as MakroKey[];
export const MIKRO_KEYS = Object.keys(MIKROS) as Mikro[];
export type WertKey = MakroKey | Mikro;

export interface Summe {
  werte: Naehrwerte;
  /** Felder, zu denen mindestens ein Posten keinen Wert hatte. */
  unvollstaendig: Set<WertKey>;
  /** Anzahl Posten (fuer „nur 2 Mahlzeiten erfasst“ zaehlt die Mahlzeit). */
  posten: number;
}

export const LEER: Summe = { werte: {}, unvollstaendig: new Set(), posten: 0 };

const r1 = (x: number) => Math.round(x * 10) / 10;

export function skalieren(w: Naehrwerte, faktor: number): Naehrwerte {
  const out: Naehrwerte = {};
  for (const k of MAKRO_KEYS) if (w[k] !== undefined) out[k] = w[k]! * faktor;
  if (w.mikro) {
    out.mikro = {};
    for (const m of MIKRO_KEYS) if (w.mikro[m] !== undefined) out.mikro[m] = w.mikro[m]! * faktor;
  }
  return out;
}

/** Summe mehrerer Teile; ein Feld ohne Wert in einem Teil macht die Summe „unvollstaendig“. */
export function addieren(teile: Summe[]): Summe {
  const werte: Naehrwerte = {};
  const unvollstaendig = new Set<WertKey>();
  let posten = 0;
  for (const t of teile) {
    posten += t.posten;
    t.unvollstaendig.forEach((k) => unvollstaendig.add(k));
  }
  for (const k of MAKRO_KEYS) {
    const da = teile.filter((t) => t.werte[k] !== undefined);
    if (da.length) werte[k] = da.reduce((s, t) => s + t.werte[k]!, 0);
    if (da.length < teile.length) unvollstaendig.add(k);
  }
  for (const m of MIKRO_KEYS) {
    const da = teile.filter((t) => t.werte.mikro?.[m] !== undefined);
    if (da.length) (werte.mikro ??= {})[m] = da.reduce((s, t) => s + t.werte.mikro![m]!, 0);
    if (da.length < teile.length) unvollstaendig.add(m);
  }
  return { werte, unvollstaendig, posten };
}

const einzeln = (werte: Naehrwerte): Summe => ({ werte, unvollstaendig: new Set(), posten: 1 });

export interface Daten { lebensmittel: ReadonlyMap<ID, Lebensmittel>; rezepte: ReadonlyMap<ID, Rezept> }

/** Ein Rezept fuer eine Portion: Summe der Zutaten geteilt durch die Portionen. */
export function rezeptProPortion(r: Rezept, d: Pick<Daten, 'lebensmittel'>): Summe {
  const teile = r.zutaten.map((z) => {
    const l = d.lebensmittel.get(z.lebensmittelId);
    return l ? einzeln(skalieren(l.je100, z.menge / 100)) : { ...LEER, posten: 1, unvollstaendig: new Set<WertKey>(MAKRO_KEYS) };
  });
  const s = addieren(teile);
  return { ...s, werte: skalieren(s.werte, 1 / Math.max(1, r.portionen)), posten: 1 };
}

export function postenWerte(p: Posten, d: Daten): Summe {
  if (p.art === 'schnell') {
    const s = einzeln(p.werte);
    // Schnelleintrag: nur das Eingetragene ist bekannt.
    for (const k of MAKRO_KEYS) if (p.werte[k] === undefined && k !== 'kcal') s.unvollstaendig.add(k);
    return s;
  }
  if (p.art === 'lebensmittel') {
    const l = d.lebensmittel.get(p.refId);
    if (!l) return { ...LEER, posten: 1, unvollstaendig: new Set<WertKey>(MAKRO_KEYS) };
    const s = einzeln(skalieren(l.je100, p.menge / 100));
    for (const m of MIKRO_KEYS) if (l.je100.mikro?.[m] === undefined) s.unvollstaendig.add(m);
    return s;
  }
  const r = d.rezepte.get(p.refId);
  if (!r) return { ...LEER, posten: 1, unvollstaendig: new Set<WertKey>(MAKRO_KEYS) };
  const pp = rezeptProPortion(r, d);
  return { ...pp, werte: skalieren(pp.werte, p.menge), posten: 1 };
}

export const mahlzeitWerte = (m: Mahlzeit, d: Daten): Summe => addieren(m.posten.map((p) => postenWerte(p, d)));
export const tagWerte = (mahlzeiten: Mahlzeit[], d: Daten): Summe => addieren(mahlzeiten.map((m) => mahlzeitWerte(m, d)));

/** Getraenke mit Naehrwerten (Saft, Softdrink): Werte pro 100 ml, Menge in ml. Ohne solche: `null`. */
export function getraenkeWerte(liste: Getraenk[], d: Daten): Summe | null {
  const mit = liste.filter((g) => g.lebensmittelId);
  return mit.length ? addieren(mit.map((g) => postenWerte({ id: g.id, art: 'lebensmittel', refId: g.lebensmittelId!, menge: g.ml }, d))) : null;
}

/** Anzeige: auf eine Nachkommastelle, kcal ganz. */
export function rund(k: WertKey, v: number | undefined): number | undefined {
  if (v === undefined) return undefined;
  return k === 'kcal' ? Math.round(v) : r1(v);
}

/** Name eines Postens fuer Listen und Suche. */
export function postenName(p: Posten, d: Daten): string {
  if (p.art === 'schnell') return p.name || 'Schnelleintrag';
  if (p.art === 'lebensmittel') return d.lebensmittel.get(p.refId)?.name ?? 'Lebensmittel gelöscht';
  return d.rezepte.get(p.refId)?.name ?? 'Rezept gelöscht';
}

/** „120 g“, „1 Banane“, „2 Portionen“. */
export function mengeText(p: Posten, d: Daten): string {
  if (p.art === 'schnell') return `${Math.round(p.werte.kcal ?? 0)} kcal`;
  if (p.art === 'rezept') return `${p.menge.toLocaleString('de-DE')} ${p.menge === 1 ? 'Portion' : 'Portionen'}`;
  const l = d.lebensmittel.get(p.refId);
  return p.anzeige ?? `${Math.round(p.menge)} ${l?.basis ?? 'g'}`;
}
